"use client";
import { BrandModel } from "@/types/brand";
import { CategoryModel } from "@/types/category";
import React, { Dispatch, SetStateAction } from "react";
import ClientLink from "../ui/client-link";
import { cn } from "@/lib/utils";
import { useParams, useSearchParams } from "next/navigation";
import { Separator } from "../ui/separator";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { Input } from "../ui/input";
import { Search } from "lucide-react";

const BrandSidebarContent = ({
  brands,
  categories,
  setOpen,
}: {
  brands: BrandModel[];
  categories: CategoryModel[];
  setOpen?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { id } = useParams();
  const t = useTranslations();
  const searchParams = useSearchParams();
  const option = searchParams.get("option") || "";

  const [searchQuery, setSearchQuery] = useState("");

  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h2 className="font-bold text-xl px-1">{t("brands")}</h2>

        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <Input
            placeholder={t("searchBrands")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-muted/50 border-none focus-visible:ring-1 focus-visible:ring-primary/20 transition-all rounded-xl"
          />
        </div>

        <div className="space-y-1 max-h-[350px] hover-only-scroll pr-1">
          {filteredBrands.length > 0 ? (
            filteredBrands.map((b) => (
              <ClientLink
                key={b.id}
                href={`/brands/${b.id}`}
                scroll={false}
                className={cn(
                  "flex items-center gap-3 p-2.5 text-md font-medium transition-all duration-200 w-full rounded-xl hover:text-primary hover:bg-primary/5",
                  {
                    "bg-primary/10 font-bold text-primary shadow-sm":
                      b.id.toString() === id,
                  },
                )}
                onClick={setOpen ? () => setOpen(false) : undefined}
              >
                <div className="relative size-10 shrink-0 overflow-hidden rounded-full border border-border bg-white shadow-xs">
                  <Image
                    src={b.logo_url}
                    alt={b.name}
                    fill
                    className="object-contain p-1"
                  />
                </div>
                <span className="truncate">{b.name}</span>
              </ClientLink>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4 italic">
              {t("noBrandsFound")}
            </p>
          )}
        </div>
      </div>
      <Separator />
      <div>
        <h2 className="font-bold mb-3 text-md px-1">{t("categories")}</h2>
        <div className="space-y-1">
          {categories?.map((category) => (
            <ClientLink
              key={category.id}
              href={`/brands/${id}?option=${category.id}`}
              scroll={false}
              className={cn(
                "block text-sm px-3 py-2 rounded-xl hover:bg-primary/5 hover:text-primary transition-all duration-200",
                {
                  "bg-primary/5 text-primary font-semibold":
                    category.id.toString() === option,
                },
              )}
            >
              {category.name}
            </ClientLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandSidebarContent;
